import React, { useState } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import CollapsibleFormElement from "../../common/collapsible-form-element";

type TitleTemplatePattern = {
  name: string;
  value: string;
};

interface TitleTemplatesProps {
  availableTemplates: TitleTemplatePattern[];
  selectedTemplates: string[];
  onChange: (templates: string[]) => void;
}

const TitleTemplates: React.FC<TitleTemplatesProps> = ({
  availableTemplates,
  selectedTemplates,
  onChange,
}) => {
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);

  const moveTemplate = (index: number, direction: "up" | "down") => {
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === selectedTemplates.length - 1)
    ) {
      return;
    }

    const newTemplates = [...selectedTemplates];
    const targetIndex = direction === "up" ? index - 1 : index + 1;

    // Swap the templates
    [newTemplates[index], newTemplates[targetIndex]] = [
      newTemplates[targetIndex],
      newTemplates[index],
    ];

    onChange(newTemplates);
  };

  // Handle drag events for reordering
  const handleDragStart = (index: number) => {
    setDraggingIndex(index);
  };

  // Only trigger on dragEnter rather than dragOver to prevent excessive updates
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); // Prevent default to allow drop
  };

  const handleDrop = (index: number) => {
    if (draggingIndex === null || draggingIndex === index) return;

    const newTemplates = [...selectedTemplates];
    // Remove dragged item
    const draggedItem = newTemplates.splice(draggingIndex, 1)[0];
    // Insert at new position
    newTemplates.splice(index, 0, draggedItem);

    onChange(newTemplates);
    setDraggingIndex(null);
  };

  const handleDragEnd = () => {
    setDraggingIndex(null);
  };

  return (
    <CollapsibleFormElement title="Title Templates">
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Select templates and arrange them by priority. The system will generate
        titles using this order.
      </Typography>

      <Box sx={{ display: "flex", gap: 2, flexDirection: "column" }}>
        {/* Available Templates section with simplified display */}
        <Box sx={{ flex: 1 }}>
          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel id="title-templates-select-label">
              Available Templates
            </InputLabel>
            <Select
              labelId="title-templates-select-label"
              multiple
              value={selectedTemplates}
              onChange={(event) => {
                const selectedTemplateNames = event.target.value as string[];
                onChange(selectedTemplateNames);
              }}
              input={<OutlinedInput label="Available Templates" />}
              displayEmpty
              renderValue={(selected) => {
                if ((selected as string[]).length === 0) {
                  return null;
                }

                return (
                  <Typography sx={{ fontSize: "0.875rem" }}>
                    {(selected as string[]).length} template(s) selected
                  </Typography>
                );
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 224,
                  },
                },
              }}
            >
              {availableTemplates.map((template) => (
                <MenuItem
                  key={template.name}
                  value={template.name}
                  sx={{
                    fontWeight: selectedTemplates.includes(template.name)
                      ? "bold"
                      : "normal",
                  }}
                >
                  {template.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Selected templates for reordering - Right Column */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Selected Templates (Priority Order)
          </Typography>
          {selectedTemplates.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.875rem", py: 1 }}
            >
              No templates selected
            </Typography>
          ) : (
            <List dense disablePadding>
              {selectedTemplates.map((templateName, index) => {
                const template = availableTemplates.find(t => t.name === templateName);
                if (!template) return null;
                
                return (
                  <ListItem
                    key={templateName}
                    draggable={true}
                    dense
                    component="div"
                    aria-label={`Template: ${template.value}`}
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={handleDragOver}
                    onDrop={() => handleDrop(index)}
                    onDragEnd={handleDragEnd}
                    sx={{
                      py: 0.5,
                      borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
                      bgcolor:
                        draggingIndex === index ? "action.hover" : "transparent",
                    }}
                  >
                    <ListItemIcon sx={{ cursor: "grab", minWidth: 28 }}>
                      <DragIndicatorIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={template.value}
                      primaryTypographyProps={{ variant: "body2" }}
                    />
                    <Box>
                      <IconButton
                        size="small"
                        onClick={() => moveTemplate(index, "up")}
                        disabled={index === 0}
                      >
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => moveTemplate(index, "down")}
                        disabled={index === selectedTemplates.length - 1}
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>
      </Box>
    </CollapsibleFormElement>
  );
};

export default TitleTemplates;
